/**
 * Alert/Reminder Button Component
 */

import { useState, useEffect } from '@wordpress/element';
import { useAuth } from '../context/AuthContext';
import apiFetch from '@wordpress/api-fetch';
import PhoneAuthModal from './PhoneAuthModal';

export default function AlertButton({ screeningId, filmTitle, screeningTime }) {
    const { isAuthenticated, getAuthHeader } = useAuth();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [alertStatus, setAlertStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [afterHoursWarning, setAfterHoursWarning] = useState(false);

    const t = window.rialtoData?.translations || {};

    // Check if user already has an alert for this screening
    useEffect(() => {
        if (!isAuthenticated) return;

        apiFetch({
            path: 'rialto/v1/alerts',
            headers: getAuthHeader(),
        }).then((res) => {
            const existing = res.alerts?.find(
                (a) => String(a.screening_id) === String(screeningId)
            );
            if (existing) {
                setIsSubscribed(true);
                setAlertStatus(existing.status);
            }
        }).catch(() => {});
    }, [isAuthenticated, screeningId]);

    const handleClick = () => {
        if (!isAuthenticated) {
            setShowAuthModal(true);
            return;
        }

        setShowOptions(!showOptions);
    };

    const subscribe = async (minutes) => {
        setLoading(true);
        setAfterHoursWarning(false);

        try {
            const res = await apiFetch({
                path: 'rialto/v1/alerts',
                method: 'POST',
                headers: getAuthHeader(),
                data: {
                    screening_id: screeningId,
                    reminder_minutes: minutes,
                },
            });

            setIsSubscribed(true);
            setAlertStatus('pending');
            setShowOptions(false);

            if (res.after_hours_warning) {
                setAfterHoursWarning(true);
            }
        } catch (err) {
            console.error('Failed to set reminder:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAuthSuccess = () => {
        setShowAuthModal(false);
        setShowOptions(true);
    };

    if (isSubscribed) {
        const statusLabel = alertStatus === 'sent'
            ? (t.reminderSent || 'Reminder Sent')
            : (t.reminderSet || 'Reminder Set!');

        return (
            <div className="rialto-alert-container">
                <button className="rialto-btn rialto-btn-primary rialto-btn-small" disabled>
                    {statusLabel}
                </button>
                {afterHoursWarning && (
                    <div className="rialto-alert-warning">
                        {t.afterHoursWarning || 'This reminder may arrive during nighttime hours'}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="rialto-alert-container">
            <button
                className="rialto-btn rialto-btn-primary rialto-btn-small"
                onClick={handleClick}
                disabled={loading}
            >
                {loading ? '...' : (t.remindMe || 'Remind Me')}
            </button>

            {showOptions && (
                <div className="rialto-alert-options">
                    <div className="rialto-alert-options-header">
                        {t.reminderOptions || 'Remind me:'}
                    </div>
                    <button
                        className="rialto-alert-option"
                        onClick={() => subscribe(10)}
                    >
                        {t['10minutes'] || '10 minutes before'}
                    </button>
                    <button
                        className="rialto-alert-option"
                        onClick={() => subscribe(30)}
                    >
                        {t['30minutes'] || '30 minutes before'}
                    </button>
                    <button
                        className="rialto-alert-option"
                        onClick={() => subscribe(60)}
                    >
                        {t['1hour'] || '1 hour before'}
                    </button>
                    <button
                        className="rialto-alert-option"
                        onClick={() => subscribe(180)}
                    >
                        {t['3hours'] || '3 hours before'}
                    </button>
                </div>
            )}

            {showAuthModal && (
                <PhoneAuthModal
                    onClose={() => setShowAuthModal(false)}
                    onSuccess={handleAuthSuccess}
                />
            )}
        </div>
    );
}
